import { Box, Button, IconButton, Input, InputAdornment, Popover, Stack, TextField, Typography } from "@mui/material"
import { MouseEvent as ReactMouseEvent, useEffect, useState } from "react"
import { COLOR } from "../../../utils/colors"
import { Add, Check, Close, FmdGood, KeyboardArrowUp, MoreVert } from "@mui/icons-material"

export type ValueType = string | number
export type OptionsType = {
  label: string,
  subLabel?: string,
  value: ValueType
}

export default function MultiSelection({
  label,
  options,
  values,
  onChangeValues,
  defaultValueNames,
  limit = 3
}: {
  label?: string,
  options?: OptionsType[],
  values?: ValueType[],
  defaultValueNames?: string[],
  onChangeValues?: (newValue: ValueType[]) => void,
  limit?: number
}) {
  const [selectedOpenOption, setSelectedOpenOption] = useState<number>(-1)
  const zones = !!values && values?.length > 0 ? values : ['']
  const [searchs, setSearchs] = useState<string[]>(zones.map(() => ''))
  const [selectedSubLabel, setSelectedSubLabel] = useState<string>('')
  const [searchingIndex, setSearchingIndex] = useState<number>(-1)

  const getSearchTextLabel = (index: number) => {
    if (index >= zones.length) {
      return
    }

    if (!!zones[index] && searchingIndex !== index) {
      const findLabel = options?.find((item) => item.value === zones[index])
      if (!!findLabel) {
        return findLabel.label
      }
    }

    return searchs[index]
  }

  const setSearchValue = (index: number, value: ValueType) => {
    const newSearch = [...searchs]
    newSearch[index] = String(value)

    setSearchs(newSearch)
  }

  const handleAddZone = () => {
    if (zones.length >= limit) {
      return
    }
    onChangeValues?.([
      ...zones,
      ''
    ])
  }

  const handleRemoveZone = (index: number) => {
    const newResult = [...zones].filter((_, idx) => idx !== index)

    onChangeValues?.(newResult)
  }

  const handleShowSearch = (index: number) => {
    setSelectedSubLabel('All')
    setSelectedOpenOption(index)
    setSearchingIndex(index)
  }

  const clearShowSearch = () => {
    setSearchingIndex(-1)
  }

  const getListSearch = (index: number) => {
    if (!searchs[index] || searchs[index] === '') {
      return (options || []).filter(
        (item) => !!selectedSubLabel && selectedSubLabel.toLowerCase() !== 'all' ? !!item.subLabel && item.subLabel === selectedSubLabel : true)
    }
    return (options || []).filter((item) => !!searchs[index] && item.label.toLowerCase().includes(searchs[index].toLowerCase()))
  }

  const handleSelectZone = (value: string, index: number) => {
    const findOption = (options || []).find((item) => item.value === value)

    if (!findOption) {
      return
    }

    const newSearchs = [...searchs]
    newSearchs[index] = ''
    setSearchs(newSearchs)

    const newValues = (values || []).map((item) => String(item || ''))
    newValues[index] = String(findOption.value)

    setSearchingIndex(-1)

    onChangeValues?.(newValues)
  }

  useEffect(() => {
    if (!defaultValueNames || defaultValueNames.length === 0 || !options || options?.length === 0) {
      return
    }

    const find = options.filter((item) => defaultValueNames.includes(item.label)).map((item) => item.value)

    if (!find || find.length === 0) {
      return
    }
    // onChangeValues?.(find)
  }, [defaultValueNames])

  const listSubLabel = (options || []).filter((item) => !!item.subLabel && item.subLabel.length > 0).map((item) => item.subLabel)
  const subLabelsOptions = Array.from(new Set(["All", ...listSubLabel]))

  return (
    <Stack sx={{ gap: '12px', }}>
      {zones.map((_, index) => (
        <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box sx={{ display: 'flex', position: 'relative', flexDirection: 'row', gap: '4px', alignItems: 'center' }}>
            <FmdGood sx={{ color: "#FF2525", mr: '4px', fontSize: "20px" }} />
            <TextField
              key={index}
              sx={{
                flex: 'auto'
              }}
              label={`Zone ${index + 1}`}
              placeholder={`Select Zone ${index + 1}`}
              autoComplete="off"
              onBlur={() => clearShowSearch()}
              onFocus={() => handleShowSearch(index)}
              value={getSearchTextLabel(index) || ''}
              onChange={(e) => setSearchValue(index, e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {index > 0 && ( // Allow removal of fields except the first
                      <IconButton onClick={() => handleRemoveZone(index)}>
                        <Close />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            {(index > 0 || zones.length === 1) && zones.length < limit && (
              <Button onClick={handleAddZone} variant="outlined" sx={{ height: '56px', borderColor: "#c4c4c4" }}>
                <Add />
              </Button>
            )}
            {zones.length > 1 && index < zones.length - 1 && selectedOpenOption !== index && (
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  position: "absolute",
                  top: "54px",
                  left: "0px"
                }}>
                <MoreVert sx={{ color: "#BCBCBC", mt: 2.5, ml: 1, fontSize: "20px" }} />
              </Stack>
            )}
          </Box>
          {selectedOpenOption === index && (
            <Stack sx={{ mb: '10px' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '4px',
                py: '4px',
              }}>
                <Box sx={{ height: '1px', flex: 'auto', background: COLOR.background.offline }}>

                </Box>
                <Box
                  onClick={() => setSelectedOpenOption(-1)}
                  sx={{
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}>
                  <Close fontSize="small" />
                </Box>
              </Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                p: '4px',
                width: '100%',
                overflowX: 'auto'
              }}>
                {subLabelsOptions.map((item, idx) => (
                  <Box key={idx} onClick={() => setSelectedSubLabel(item || 'All')} sx={{
                    px: '16px',
                    py: '6px',
                    flex: 'none',
                    borderRadius: '999px',
                    backgroundColor: item === selectedSubLabel ? '#00E58E' : COLOR.white,
                    color: item === selectedSubLabel ? '#087D46' : COLOR.black,
                    userSelect: 'none',
                    cursor: 'pointer'
                  }}>
                    <Typography sx={{ fontSize: '12px' }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  borderRadius: '8px',
                  maxHeight: '180px',
                  overflowY: 'auto',
                  mt: '10px',
                  p: '4px',
                  border: `1px solid ${COLOR.gray[400]}`
                }}>
                {getListSearch(index).map((item, idx) => (
                  <Box key={idx} onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelectZone(String(item.value), index)
                  }} sx={{ p: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {!!item.subLabel && (
                        <div>
                          <Typography sx={{ fontSize: '12px', color: COLOR.gray[400] }}>
                            {item.subLabel}
                          </Typography>
                        </div>
                      )}
                      <Typography>
                        {item.label}
                      </Typography>
                    </Box>

                    {zones[index] === item.value && (
                      <Box>
                        <Check fontSize="small" sx={{ color: COLOR.accent.green }} />
                      </Box>
                    )}
                  </Box>
                ))}
                {getListSearch(index).length === 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                    <Typography sx={{ fontSize: '14px', color: COLOR.gray[500] }}>No Data found</Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  )
}